import { JOURNEY_STEP_TYPES, JourneyStepType } from "./journeyStepTypes";

const isProduction = (): boolean =>
  typeof process !== "undefined" && !!process.env && process.env.NODE_ENV === "production";

const warnInDev = (...args: unknown[]): void => {
  if (!isProduction() && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn("[journeyEventBuilder]", ...args);
  }
};

export interface JourneyEventIdentifiers {
  sessionId?: string;
  callId?: string;
  clientJourneyId?: string;
}

export interface JourneyEvent {
  stepType: JourneyStepType;
  stepName: string;
  sessionId?: string;
  callId?: string;
  clientJourneyId?: string;
  metadata: Record<string, unknown>;
}

/**
 * Builds a JourneyEvent forwarded to Datadog RUM. 
 * The only validation is that `stepType` is a recognized
 * JOURNEY_STEP_TYPES value (so we always emit a coherent, typed event) and
 * `stepName` is a non-empty string.
 *
 * Never throws; malformed input results in `null` so the caller can skip
 * emission without crashing.
 *
 * @param stepType - one of JOURNEY_STEP_TYPES
 * @param stepName - human-readable label for the event
 * @param rawMetadata - metadata forwarded as-is
 * @param identifiers - correlation identifiers
 */
export const buildJourneyEvent = (
  stepType: JourneyStepType,
  stepName: string,
  rawMetadata?: Record<string, unknown> | null,
  identifiers: JourneyEventIdentifiers = {}
): JourneyEvent | null => {
  try {
    const isKnownStepType = Object.values(JOURNEY_STEP_TYPES).includes(stepType);
    if (!isKnownStepType || typeof stepName !== "string" || stepName.length === 0) {
      return null;
    }

    const { sessionId, callId, clientJourneyId } = identifiers;

    return {
      stepType,
      stepName,
      sessionId: sessionId || undefined,
      callId: callId || undefined,
      clientJourneyId,
      metadata: { ...(rawMetadata || {}) },
    };
  } catch (error) {
    warnInDev("Failed to build journey event; skipping capture.", error);
    return null;
  }
};
